import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { Th } from './Table.tsx';
import { cx } from './cx.ts';

/**
 * Clickable column header for a sortable table (#1938).
 *
 * The whole header is a button so keyboard users get the same affordance as
 * the pointer, and `aria-sort` lands on the `<th>` rather than on the button —
 * assistive tech reads the sort state off the column, not off the control.
 * The indicator is always rendered (neutral when the column is inactive) so
 * the header row does not reflow as the operator moves between columns.
 */
export interface TableSort<K extends string> {
  readonly key: K;
  readonly dir: 'asc' | 'desc';
}

interface Props<K extends string> {
  readonly label: string;
  readonly sortKey: K;
  /** `null` = no column is sorted yet; every header renders neutral. */
  readonly sort: TableSort<K> | null;
  readonly onSort: (key: K) => void;
  /** Right-aligned mono column (money, counts) — mirrors `Th`'s `num`. */
  readonly num?: boolean;
  readonly disabled?: boolean;
  readonly testId?: string;
  readonly className?: string;
}

export function SortableTh<K extends string>({
  label,
  sortKey,
  sort,
  onSort,
  num = false,
  disabled = false,
  testId,
  className,
}: Props<K>): JSX.Element {
  const active = sort !== null && sort.key === sortKey;
  const ascending = active && sort.dir === 'asc';
  const Indicator = active ? (ascending ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <Th
      num={num}
      className={className}
      aria-sort={active ? (ascending ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={() => onSort(sortKey)}
        data-testid={testId}
        className={cx(
          'inline-flex items-center gap-1 rounded text-label font-medium transition-colors duration-120 hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40 disabled:pointer-events-none disabled:opacity-50',
          num && 'flex-row-reverse',
          active ? 'text-paper' : 'text-paper-low',
        )}
      >
        <span>{label}</span>
        <Indicator aria-hidden="true" className={cx('h-3 w-3', !active && 'opacity-50')} />
      </button>
    </Th>
  );
}
